import { CHAIN_MAPPING, KNOWN_CHAINS, chainsEnum } from "./chains";
import { numToStr } from "./type-safe-stringify";
import type { TokensFromOneClickApi } from "./tokens-from-one-click-api";

import tokens from "./tokens.json" assert { type: "json" };

// icon is a file in each app's public TOKEN_ICON_PATH; tokens without one get a letter icon.
export const TOKENS: Record<
	string,
	{
		chain: string;
		decimals: number;
		symbol: string;
		icon?: string;
	}
> = tokens;

export const TOKEN_ICON_PATH = "/icons/token/";

export type Tokens = typeof TOKENS;

// 1Click symbols that update-tokens replaces, e.g. names 1Click marks as deprecated.
const SYMBOL_OVERRIDES: ReadonlyMap<string, string> = new Map([
	["1cs_v1:btc:native:coin", "BTC"],
	["1cs_v1:bitcoin:native:coin", "BTC"],
	["1cs_v1:ton:native:coin", "GRAM"],
	["nep141:btc.stft.near", "BTC"],
	["nep245:v2_1.omni.hot.tg:1117_", "GRAM"],
	["nep245:v2_1.omni.hot.tg:9745_11111111111111111111", "XPL"],
	["nep245:v2_1.omni.hot.tg:9745_3aL9skCy1yhPoDB8oKMmRHRN7SJW", "USDT"],
]);

// 1Click symbols renamed on every token, e.g. USDT0, which the swap app shows as USDT.
const SYMBOL_RENAMES: ReadonlyMap<string, string> = new Map([
	["USDT0", "USDT"],
]);

// How update-tokens and the runtime both read a 1Click token: chain names mapped, symbols overridden.
export function fromOneClickToken(
	id: string,
	{ chain, decimals, symbol }: TokensFromOneClickApi[string],
) {
	return {
		chain: CHAIN_MAPPING.get(chain) ?? chain,
		decimals,
		symbol: SYMBOL_OVERRIDES.get(id) ?? SYMBOL_RENAMES.get(symbol) ?? symbol,
	};
}

// The tokens the next update-tokens run would add or change: 1Click's chain, symbol and decimals,
// mapped like the script does. Icons stay; a new token borrows one by symbol.
export function getTokenUpdates(
	tokensFromOneClickApi: TokensFromOneClickApi,
): Tokens {
	const iconBySymbol = new Map<string, string>();

	for (const { symbol, icon } of Object.values(TOKENS)) {
		if (icon !== undefined && !iconBySymbol.has(symbol)) {
			iconBySymbol.set(symbol, icon);
		}
	}

	const tokenUpdates = Object.entries(tokensFromOneClickApi)
		.map(([id, apiToken]) => {
			const { chain, decimals, symbol } = fromOneClickToken(id, apiToken);

			// Only new tokens borrow: a known token without an icon keeps its letter icon.
			const icon = Object.hasOwn(TOKENS, id)
				? TOKENS[id]?.icon
				: iconBySymbol.get(symbol);

			return [
				id,
				{ chain, decimals, symbol, ...(icon === undefined ? {} : { icon }) },
			] as const;
		})
		.filter(([id, token]) => {
			const existing = TOKENS[id];

			return (
				existing === undefined ||
				existing.chain !== token.chain ||
				existing.decimals !== token.decimals ||
				existing.symbol !== token.symbol
			);
		});

	return Object.fromEntries(tokenUpdates);
}

const TOKEN_IDS_HIDDEN_FROM_FILTERS = [
	"nep141:base-0xa5c67d8d37b88c2d88647814da5578128e2c93b2.omft.near",
	"nep141:pol.omft.near",
	"nep141:stjack.tkn.primitives.near",
	"nep141:test-token.highdome3013.near",
	"nep141:853d955acef822db058eb8505911ed77f175b99e.factory.bridge.near",
	"nep141:2260fac5e5542a773aa44fbcfedf7c193bc2c599.factory.bridge.near",
	"nep141:token.burrow.near",
	"nep141:bsc.omft.near",
	"nep141:bsc-0x8ac76a51cc950d9822d68b83fe1ad97b32cd580d.omft.near",
	"nep141:bsc-0x55d398326f99059ff775485246999027b3197955.omft.near",
	"nep141:aurora",
	"nep141:token.v2.ref-finance.near",
];

// KNOWN_CHAINS keeps old 1Click names (solana, bitcoin, ...) for history; only delisted tokens use
// them. A chain missing from KNOWN_CHAINS is new, so it stays.
function isLegacyChain(chain: string) {
	return (
		Object.hasOwn(KNOWN_CHAINS, chain) && !chainsEnum.safeParse(chain).success
	);
}

function getFilterableTokens(tokens: Tokens) {
	return Object.entries(tokens)
		.filter(([id, { chain }]) => {
			return (
				!isLegacyChain(chain) && !TOKEN_IDS_HIDDEN_FROM_FILTERS.includes(id)
			);
		})
		.sort(([, a], [, b]) => {
			return a.symbol.localeCompare(b.symbol);
		});
}

export function getTokenIdsByChain(tokens: Tokens): Record<string, string[]> {
	// A Map, so a chain named like an Object.prototype key ("constructor") is just a key.
	const tokenIdsByChain = new Map<string, string[]>();

	for (const [id, { chain }] of getFilterableTokens(tokens)) {
		tokenIdsByChain.set(chain, [...(tokenIdsByChain.get(chain) ?? []), id]);
	}

	return Object.fromEntries(tokenIdsByChain);
}

export function chainLabel(chain: string): string {
	return KNOWN_CHAINS[chain]?.label ?? chain.toUpperCase();
}

export function tokenLabel({
	symbol,
	chain,
}: {
	symbol: string;
	chain: string;
}): string {
	return `${symbol} (${chainLabel(chain)})`;
}

// Object.hasOwn, so ids from URLs like "constructor" don't hit Object.prototype.
export function getTokenLabel(tokens: Tokens, id: string): string | undefined {
	const token = Object.hasOwn(tokens, id) ? tokens[id] : undefined;
	return token === undefined ? undefined : tokenLabel(token);
}

interface VisibleTokenDisplay {
	id: string;
	symbol: string;
	chain: string;
}

export interface FilterTokens {
	// the dropdown's label group, not a generic label index
	tokenIdsByLabel: ReadonlyMap<string, readonly string[]>;
	tokenDisplayByChain: ReadonlyMap<string, readonly VisibleTokenDisplay[]>;
	chains: readonly { id: string; name: string }[];
}

// Filters list only the tokens found in the 1Click DB.
export function getFilterTokens(
	tokens: Tokens,
	tokenIdsFromOneClickDB: readonly string[],
): FilterTokens {
	const inOneClickDB = new Set(tokenIdsFromOneClickDB);

	const visibleTokens = getFilterableTokens(tokens).filter(([id]) => {
		return inOneClickDB.has(id);
	});

	const tokenIdsByLabel = visibleTokens.reduce<Map<string, string[]>>(
		(acc, [id, token]) => {
			const key = tokenLabel(token);
			return acc.set(key, [...(acc.get(key) ?? []), id]);
		},
		new Map(),
	);

	const tokenDisplayByChain = new Map<string, VisibleTokenDisplay[]>();
	const seenLabels = new Set<string>();

	for (const [id, token] of visibleTokens) {
		const label = tokenLabel(token);

		// Tokens sharing a label collapse to one entry.
		if (seenLabels.has(label)) {
			continue;
		}

		seenLabels.add(label);
		const list = tokenDisplayByChain.get(token.chain) ?? [];
		list.push({ id, symbol: token.symbol, chain: token.chain });
		tokenDisplayByChain.set(token.chain, list);
	}

	const chains = [...tokenDisplayByChain.keys()]
		.map((chainId) => {
			return {
				id: chainId,
				name: chainLabel(chainId),
			};
		})
		.sort((a, b) => {
			return a.name.localeCompare(b.name);
		});

	return { tokenIdsByLabel, tokenDisplayByChain, chains };
}

// One hue per token, lightened for the background and darkened for the letter. OKLCH lightness is
// perceptual, so every hue looks the same. At these values both colours stay in sRGB for every hue
// and the letter keeps at least 5.7:1 contrast (WCAG AA is 4.5:1).
export const LETTER_ICON_OKLCH = {
	background: { lightness: 0.88, chroma: 0.05 },
	letter: { lightness: 0.42, chroma: 0.07 },
};

function oklch(
	{ lightness, chroma }: { lightness: number; chroma: number },
	hue: number,
) {
	return `oklch(${numToStr(lightness)} ${numToStr(chroma)} ${numToStr(hue)})`;
}

// First and last word's initials, or a single word's first two characters. Words split on
// punctuation and on lower-to-upper case changes: "wNEAR" gives WN, "USDC.e" UE, "QTC" QT.
function getInitials(name: string): string {
	const words =
		name.replace(/(\p{Ll})(\p{Lu})/gu, "$1 $2").match(/[\p{L}\p{N}]+/gu) ?? [];

	const first = words[0]?.match(/./gu) ?? [];
	const last = words.at(-1)?.match(/./gu) ?? [];

	const initials =
		words.length > 1
			? `${first[0] ?? ""}${last[0] ?? ""}`
			: first.slice(0, 2).join("");

	// No letters or digits, like the "*" wildcard: show its punctuation or symbols instead.
	const shown =
		initials === ""
			? (name.match(/[\p{P}\p{S}]/gu) ?? []).slice(0, 2).join("")
			: initials;

	return (shown.toUpperCase().match(/./gu) ?? []).slice(0, 2).join("");
}

// A rounded square with the initials of `name`, for tokens and chains without artwork.
// Coloured by name, so tokens sharing a symbol across chains look alike.
export function getLetterIcon(name: string): string {
	const initials = getInitials(name);
	let hue = 0;

	for (const char of name.toUpperCase()) {
		hue = (hue * 31 + char.charCodeAt(0)) % 360;
	}

	const { background, letter: letterColor } = LETTER_ICON_OKLCH;
	const fontSize = initials.length > 1 ? "13" : "16";

	const text = initials
		.replaceAll("&", "&amp;")
		.replaceAll("<", "&lt;")
		.replaceAll(">", "&gt;");

	const svg = `<svg xmlns='http://www.w3.org/2000/svg' width='32' height='32' viewBox='0 0 32 32'><rect width='32' height='32' rx='8' fill='${oklch(background, hue)}'/><text x='16' y='16' dy='0.35em' text-anchor='middle' font-family='sans-serif' font-size='${fontSize}' font-weight='600' fill='${oklch(letterColor, hue)}'>${text}</text></svg>`;

	return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
}

// The token's icon file (tokens.json, or borrowed for one only 1Click lists), else a letter icon.
export function getTokenIcon(tokens: Tokens, defuseAssetId: string): string {
	const token = Object.hasOwn(tokens, defuseAssetId)
		? tokens[defuseAssetId]
		: undefined;

	return token?.icon === undefined
		? getLetterIcon(
				token?.symbol ?? defuseAssetId.slice(defuseAssetId.indexOf(":") + 1),
			)
		: `${TOKEN_ICON_PATH}${token.icon}`;
}

// For a token missing from tokens.json: the icon of a listed token with that symbol, else a letter icon.
export function getSymbolIcon(tokens: Tokens, symbol: string): string {
	const icon = Object.values(tokens).find((token) => {
		return token.symbol === symbol && token.icon !== undefined;
	})?.icon;

	return icon === undefined
		? getLetterIcon(symbol)
		: `${TOKEN_ICON_PATH}${icon}`;
}
